//CRN_20251218 - ⚠️ ngOnInit() called for components/directives & not services ⚠️

import { BooleanResultDTO } from 'src/app/core/interfaces/general/boolean-result';
import { EncryptionService } from '../../general/encryption-service';
import { environment } from 'src/environments/environment';
import { eSimApiPaths } from 'src/app/core/consts';
import { ExceptionsService } from '../../general/exceptions-service';
import { forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { Guid } from 'guid-typescript';
import { HttpClientService } from '../../general/http-client-service';
import { IdAndDescriptionDTO } from 'src/app/core/interfaces/general/id-description';
import { Injectable } from '@angular/core';
import { IntegerResultDTO } from 'src/app/core/interfaces/general/integer-result';
import { StringResultDTO } from 'src/app/core/interfaces/general/string-result';
import { UserChannelAvailabilityDTO } from 'src/app/core/models/system/user/user-channel-availability.dto';
import { UserDTO } from 'src/app/core/models/system/user/user.dto';
import { UserIdDescriptionDTO } from 'src/app/core/models/system/user/userIdDescription.dto';
import { UserIdFullnameDTO } from 'src/app/core/models/system/user/userIdFullname.dto';
import { UserMobileConflictDTO } from 'src/app/core/models/system/user/user-mobile-conflict.dto';
import { UserMobileE164RepairResultDTO } from 'src/app/core/models/system/user/user-mobile-e164-repair.dto';

@Injectable({ providedIn: 'root' })

export class UsersService {
  
  constructor(
    private encryptionService: EncryptionService,
    private exceptionsService: ExceptionsService,
    private httpClientService: HttpClientService
  ) { }

  //#region General User methods
    
  public cleanGuid(user: UserDTO) {
    try {
      user.guid=user.guid != undefined && <Guid>user.guid["value"] != undefined ? user.guid["value"] : user.guid;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public channelAvailabilityForHierarchy(companyId: number | undefined): Observable<UserChannelAvailabilityDTO[]> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>(companyId ?? -1), true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEncSS=encResult.result;

                            return this.httpClientService.get<UserChannelAvailabilityDTO[]>(environment.eSimApi +
                                                                                            eSimApiPaths.Users + "/" +
                                                                                            eSimApiPaths.UsersChannelAvailabilityForHierarchy +
                                                                                            "?companyIdEncSS=" + companyIdEncSS);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public mobileExistsOnDifferentUser(inviewUserId: number, mobile: string): Observable<UserMobileConflictDTO> {
    try {
      return forkJoin({
               inviewUserIdEncResult: this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>inviewUserId, true),
               mobileEncResult      : this.encryptionService.encrypt_AesServerSideForWeb(mobile)
             })
             .pipe(
               switchMap(({ inviewUserIdEncResult, mobileEncResult }) => {
                 const inviewUserIdEncSS=inviewUserIdEncResult.result;
                 const mobileEnc        =mobileEncResult.result;

                 return this.httpClientService.get<UserMobileConflictDTO>(environment.eSimApi +
                                                                          eSimApiPaths.Users + "/" +
                                                                          eSimApiPaths.UsersMobileExistsOnDifferentUser +
                                                                          "?inviewUserIdEncSS=" + inviewUserIdEncSS +
                                                                          "&mobileEnc=" + mobileEnc);
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getDomainFromUsername(username: null | string | undefined): string | undefined {
    try {
      if (!username || typeof username !== 'string')
        return undefined;
  
      const atIndex=username.indexOf('@');
  
      if (atIndex<=0 || atIndex===username.length-1)
        return undefined;
  
      return username.substring(atIndex+1);
    }
    catch (e) {
      this.exceptionsService.handleException(<Error>e);
      return undefined;
    }
  }

  //#endregion
    
  public delete(id: number): Observable<UserDTO> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>id, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const idEncSS=encResult.result;
                            
                            return this.httpClientService.delete(environment.eSimApi +
                                                                 eSimApiPaths.Users +
                                                                 "?idEncSS=" + idEncSS, undefined);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getById(id: number): Observable<UserDTO> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>id, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const idEncSS=encResult.result;
                            
                            return this.httpClientService.getById(environment.eSimApi +
                                                                  eSimApiPaths.Users +
                                                                  "?idEncSS=" + idEncSS, undefined); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getCompanyId(userId: number): Observable<IntegerResultDTO> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>userId, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const userIdEncSS=encResult.result;
                            
                            return this.httpClientService.get<IntegerResultDTO>(environment.eSimApi +
                                                                                eSimApiPaths.Users + "/" +
                                                                                eSimApiPaths.UsersGetCompanyId +
                                                                                "?userIdEncSS=" + userIdEncSS, undefined); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getIdAndDescriptionByCompanyId(companyId: number): Observable<IdAndDescriptionDTO[]> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>companyId, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEncSS=encResult.result;
                            
                            return this.httpClientService.get<UserDTO[]>(environment.eSimApi +
                                                                         eSimApiPaths.Users + "/" +
                                                                         eSimApiPaths.UsersGetUsersForCompany +
                                                                         "?companyIdEncSS=" + companyIdEncSS)
      .pipe(
        map(x => x.map(this.idAndDescriptionConverter))
      ); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getIdAndDescriptionForHierarchy(companyId: number | undefined) {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>(companyId ?? -1), true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEncSS=encResult.result;
                            
                            return this.httpClientService.get<UserIdDescriptionDTO[]>(environment.eSimApi +
                                                                                      eSimApiPaths.Users + "/" +
                                                                                      eSimApiPaths.HierarchyIdDescription +
                                                                                      "?companyIdEncSS=" + companyIdEncSS)
                                   .pipe(
                                     map(x => x.map(this.idAndDescriptionConverter))
                                   ); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getIdAndFullNameByCompanyId(companyId: number): Observable<IdAndDescriptionDTO[]> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>companyId, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEncSS=encResult.result;
                            
                            return this.httpClientService.get<UserDTO[]>(environment.eSimApi +
                                                                         eSimApiPaths.Users + "/" +
                                                                         eSimApiPaths.UsersGetUsersForCompany +
                                                                         "?companyIdEncSS=" + companyIdEncSS)
                                   .pipe(
                                     map(x => x.map(this.idAndFullnameConverter))
                                   ); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getIdByCompanyIdAndFullname(companyId: number, fullname: string): Observable<IntegerResultDTO> {
    try {
      return forkJoin({
               companyIdEncResult: this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>companyId, true),
               fullnameEncResult : this.encryptionService.encrypt_AesServerSideForWeb(fullname)
             })
             .pipe(
               switchMap(({ companyIdEncResult, fullnameEncResult }) => {
                 const companyIdEncSS=companyIdEncResult.result;
                 const fullnameEnc   =fullnameEncResult.result;
                 
                 return this.httpClientService.getByDescription(environment.eSimApi +
                                                                eSimApiPaths.Users + "/" +
                                                                eSimApiPaths.GetIdByCompanyIdAndFullname +
                                                                "?companyIdEncSS=" + companyIdEncSS +
                                                                "&fullnameEnc="  + fullnameEnc,
                                                                undefined
                                                               );
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getIdByUsername(companyId: number, username: string): Observable<IntegerResultDTO> {
    try {
      return forkJoin({
               companyIdEncResult: this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>companyId, true),
               usernameEncResult : this.encryptionService.encrypt_AesServerSideForWeb(username)
             })
             .pipe(
               switchMap(({ companyIdEncResult, usernameEncResult }) => {
                 const companyIdEncSS=companyIdEncResult.result;
                 const usernameEnc   =usernameEncResult.result;
                 
                 return this.httpClientService.getByDescription(environment.eSimApi +
                                                                eSimApiPaths.Users + "/" +
                                                                eSimApiPaths.GetIdByUsername +
                                                                "?companyIdEncSS=" + companyIdEncSS +
                                                                "&usernameEnc="    + usernameEnc,
                                                                undefined
                                                               );
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getUsersAssociate(companyId: number, includeDeleted: boolean=false, includeCeased: boolean=false): Observable<UserDTO[]> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>companyId, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEncSS=encResult.result;

                            return this.httpClientService.get<UserDTO[]>(environment.eSimApi +
                                                                         eSimApiPaths.Users + "/" +
                                                                         eSimApiPaths.GetUsersAssociate +
                                                                         "?companyIdEncSS=" + companyIdEncSS +
                                                                         (includeDeleted ? "&includeDeleted=true" : "") +
                                                                         (includeCeased  ? "&includeCeased=true"  : ""));
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getUsersESim(companyId: number, includeDeleted: boolean=false, includeCeased: boolean=false): Observable<UserDTO[]> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>companyId, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEncSS=encResult.result;

                            return this.httpClientService.get<UserDTO[]>(environment.eSimApi +
                                                                         eSimApiPaths.Users + "/" +
                                                                         eSimApiPaths.GetUsersESim +
                                                                         "?companyIdEncSS=" + companyIdEncSS +
                                                                         (includeDeleted ? "&includeDeleted=true" : "") +
                                                                         (includeCeased  ? "&includeCeased=true"  : ""));
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getUsersForCompanyIncAssociates(companyId: number, excludeIotDevices: boolean=false): Observable<UserDTO[]> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>companyId, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEncSS=encResult.result;

                            return this.httpClientService.get<UserDTO[]>(environment.eSimApi +
                                                                         eSimApiPaths.Users + "/" +
                                                                         eSimApiPaths.UsersGetUsersForCompanyIncAssociates +
                                                                         "?companyIdEncSS=" + companyIdEncSS +
                                                                         (excludeIotDevices ? "&excludeIotDevices=true" : ""));
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getUsersPortal(companyId: number, includeDeleted: boolean=false, includeCeased: boolean=false): Observable<UserDTO[]> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>companyId, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEncSS=encResult.result;

                            return this.httpClientService.get<UserDTO[]>(environment.eSimApi +
                                                                         eSimApiPaths.Users + "/" +
                                                                         eSimApiPaths.GetUsersPortal +
                                                                         "?companyIdEncSS=" + companyIdEncSS +
                                                                         (includeDeleted ? "&includeDeleted=true" : "") +
                                                                         (includeCeased  ? "&includeCeased=true"  : ""));
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getUsersWithUnresolvedMobile(companyId: number | undefined): Observable<UserDTO[]> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>(companyId ?? -1), true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEncSS=encResult.result;

                            return this.httpClientService.get<UserDTO[]>(environment.eSimApi +
                                                                         eSimApiPaths.Users + "/" +
                                                                         eSimApiPaths.UsersGetUsersWithUnresolvedMobile +
                                                                         "?companyIdEncSS=" + companyIdEncSS);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  private idAndDescriptionConverter(user: UserIdDescriptionDTO) {
    const description=user.iotDevice ? (user.firstname ?? "") + " - " + (user.lastname ?? "") : user.username;

    return {id: user.id, description: description};
  }

  private idAndFullnameConverter(user: UserIdFullnameDTO) {
    return {id: user.id, description: user.fullname};
  }

  public killUser(guid: Guid): Observable<BooleanResultDTO> {
    try {
      return this.httpClientService.delete(environment.eSimApi +
                                           eSimApiPaths.Users + "/" +
                                           eSimApiPaths.UsersKillUser +
                                           "?guid=" + guid);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public post(user: UserDTO): Observable<UserDTO> {
    try {
      //JSON.stringify(user)
      this.cleanGuid(user);
      
      return this.encryptionService.encrypt_AesServerSideForWeb(JSON.stringify(user), true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const userEncSS=encResult.result;
                            
                            return this.httpClientService.post(environment.eSimApi +
                                                               eSimApiPaths.Users +
                                                               "?userEncSS=" + userEncSS, {});
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public put(user: UserDTO): Observable<UserDTO> {
    try {
      //JSON.stringify(user)
      this.cleanGuid(user);
      
      return this.encryptionService.encrypt_AesServerSideForWeb(JSON.stringify(user), true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const userEncSS=encResult.result;
                            
                            return this.httpClientService.put(environment.eSimApi +
                                                              eSimApiPaths.Users +
                                                              "?id=" + user.id +
                                                              "&userEncSS=" + userEncSS, undefined, {}); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public repairMalformedMobileE164(): Observable<UserMobileE164RepairResultDTO> {
    try {
      return this.httpClientService.post(environment.eSimApi
                                         + eSimApiPaths.Users
                                         + "/"
                                         + eSimApiPaths.UsersRepairMalformedMobileE164, {});
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public runMobileNumberMigration(): Observable<BooleanResultDTO> {
    try {
      return this.httpClientService.post(environment.eSimApi +
                                         eSimApiPaths.Users + "/" +
                                         eSimApiPaths.UsersNormaliseMobileNumbers, {});
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public save(user: UserDTO): Observable<any> {
    try {
      //JSON.stringify(user);
      if (user.id && <number>user.id>0)
        return this.put(user);
  
      return this.post(user);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public transferUser(companyId: number, userId: number): Observable<BooleanResultDTO> {
    try {
      return forkJoin({
               companyIdEncResult: this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>companyId, true),
               userIdEncResult   : this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>userId)
             })
             .pipe(
               switchMap(({ companyIdEncResult, userIdEncResult }) => {
                 const companyIdToEncSS=companyIdEncResult.result;
                 const userIdEnc       =userIdEncResult.result;

                 return this.httpClientService.post(environment.eSimApi +
                                                    eSimApiPaths.Users + "/" +
                                                    eSimApiPaths.UsersTransferUser +
                                                    "?companyIdToEncSS=" + companyIdToEncSS +
                                                    "&userIdEnc="        + userIdEnc,
                                                    {}
                                                   );
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public transferUserSimData(userFrom: string, userTo: string, curUserId: number): Observable<BooleanResultDTO> {
    try {
      return forkJoin({
               idEncResult      : this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>curUserId),
               userFromEncResult: this.encryptionService.encrypt_AesServerSideForWeb(userFrom, true),
               userToEncResult  : this.encryptionService.encrypt_AesServerSideForWeb(userTo)
             })
             .pipe(
               switchMap(({ idEncResult, userFromEncResult, userToEncResult }) => {
                 const idEnc        =idEncResult.result;
                 const userFromEncSS=userFromEncResult.result;
                 const userToEnc    =userToEncResult.result;
                 
                 return this.httpClientService.post(environment.eSimApi +
                                                    eSimApiPaths.Users + "/" +
                                                    eSimApiPaths.UsersTransferUserSimData +
                                                    "?userFromEncSS=" + userFromEncSS +
                                                    "&userToEnc="     + userToEnc +
                                                    "&idEnc="         + idEnc,
                                                    {}
                                                   );
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public undelete(id: number): Observable<any> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>id, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const idEncSS=encResult.result;

                            return this.httpClientService.put(environment.eSimApi +
                                                              eSimApiPaths.Users + "/" +
                                                              eSimApiPaths.UsersUndelete +
                                                              "?idEncSS=" + idEncSS, undefined, undefined);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
}
